import { FdrProductFeature } from '@hotelplan/supergraph-api';
import { TWishlistSelectedItems } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import {
  isFdrWishlistProduct,
  isFdrWishlistProductCruise,
  isFdrWishlistProductHotel,
  isFdrWishlistProductRoundTrip,
} from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist.types';
import { getFdrProductBreadcrumbs } from 'fdr/components/local/fdr-page-components/recommendations/product/fdr-product-recommendation.utils';
import { getFdrProductIcons } from 'fdr/components/local/fdr-travel-types/fdr-travel-type.utils';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import { TFdrWishlistCompareItem } from './fdr-wishlist-compare-items.types';
import { IWishlistProductFeatureIconProps } from './fdr-wishlist.types';

function getFeatureIconAndColor(
  feature: FdrProductFeature,
  item: FdrWishlistItemFragment
): IWishlistProductFeatureIconProps {
  const isNA = item
    ? isFdrWishlistProductRoundTrip(item?.product) ||
      isFdrWishlistProductCruise(item?.product)
    : false;

  const isEnabled = isFdrWishlistProduct(item?.product)
    ? (isFdrWishlistProductHotel(item.product)
        ? item.product?.descriptionFeatureGroups || []
        : []
      ).some(g => g.features.some(f => f.id === feature.id && f.enabled))
    : false;

  return {
    name: isNA ? 'circleminus' : isEnabled ? 'success' : 'cancel',
    color: isNA ? 'secondary' : isEnabled ? 'success' : 'error',
    isVisible: !!item,
    isNA,
  };
}

export function getFeatureIconAndColorForCompareItem(
  feature: FdrProductFeature,
  item: TFdrWishlistCompareItem
): IWishlistProductFeatureIconProps {
  const isNA = item ? !item?.productId?.includes('h') : false;

  return {
    name: isNA ? 'circleminus' : 'cancel',
    color: isNA ? 'secondary' : 'error',
    isVisible: !!item,
    isNA,
  };
}

export function getOfferFromWishlistItem(item: FdrWishlistItemFragment) {
  return item?.trackedOffer.latest
    ? item?.trackedOffer.latest
    : item?.trackedOffer.original;
}

export function getOfferLinkFromWishlistItem(item: FdrWishlistItemFragment) {
  return item?.trackedOffer.latest
    ? item?.trackedOffer.latest.webMeta.link
    : item?.product.webMeta.offersWebMeta?.link;
}

export const mapWishlistSelectedItemToCompareItem = (
  selectedItem: TWishlistSelectedItems[number]
): TFdrWishlistCompareItem => {
  if (!isFdrWishlistProduct(selectedItem?.product)) return null;

  let offerToShow = selectedItem.trackedOffer.original;

  if (selectedItem.trackedOffer.modified) {
    offerToShow = selectedItem.trackedOffer.modified;
  } else if (selectedItem.trackedOffer.latest) {
    offerToShow = selectedItem.trackedOffer.latest;
  }

  return {
    productId: selectedItem.product.id,
    status: selectedItem.trackedOffer.status,
    name: selectedItem.product.name,
    image: selectedItem.product?.hero,
    summaryFeatureGroups: selectedItem.product?.summaryFeatureGroups || [],
    descriptionFeatureGroups:
      (isFdrWishlistProductHotel(selectedItem.product)
        ? selectedItem.product?.descriptionFeatureGroups || []
        : []
      )?.map(feature => ({
        ...feature,
        features: feature.features.map(f => ({
          ...f,
          icon: getFeatureIconAndColor(f, selectedItem),
        })),
      })) || [],
    productTypeData: getFdrProductIcons(
      offerToShow.travelType,
      selectedItem.product.__typename
    ),
    link:
      offerToShow.webMeta.link ||
      selectedItem.product.webMeta?.offersWebMeta?.link,
    breadcrumbs: getFdrProductBreadcrumbs(selectedItem.product),
    productLink: selectedItem.product.webMeta?.offersWebMeta?.link,
    // hotel specific properties
    ...(isFdrWishlistProductHotel(selectedItem.product) && {
      rating: selectedItem.product.rating,
      taRating: selectedItem.product?.tripAdvisorReviews?.totalRating || 0,
      taReviews: selectedItem.product?.tripAdvisorReviews?.numReviews || 0,
    }),
    // Round trip and cruise specific properties
    ...((isFdrWishlistProductRoundTrip(selectedItem.product) ||
      isFdrWishlistProductCruise(selectedItem.product)) && {
      includedInPrice: selectedItem.product?.includedInPrice,
      taRating: selectedItem.product?.hubData?.taRating || 0,
      taReviews: selectedItem.product?.hubData?.taReviews || 0,
    }),
    ...offerToShow,
  };
};
